import { useEffect, useRef, useState } from 'react';

interface UseDialogFocusProps {
  isOpen: boolean;
  modalId: number | string;
  currentId: number | string;
}

export const useDialogFocus = ({ isOpen, modalId, currentId }: UseDialogFocusProps) => {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const dialogRef = useRef<HTMLElement | null>(null);
  const inputsRef = useRef<HTMLElement[]>([]);

  const handleFocusIn = () => {
    setIsInputFocused(true);
  };

  const handleFocusOut = (event: FocusEvent) => {
    const relatedTarget = event.relatedTarget as HTMLElement;
    const isNextElementTrackedInput = inputsRef.current.includes(relatedTarget);

    if (!isNextElementTrackedInput) {
      setIsInputFocused(false);
    }
  };

  const addInputListeners = (dialog: HTMLElement) => {
    const inputs = Array.from(dialog.querySelectorAll('input, textarea, select')) as HTMLElement[];

    inputs.forEach(input => {
      input.addEventListener('focusin', handleFocusIn);
      input.addEventListener('focusout', handleFocusOut as EventListener);
    });

    inputsRef.current = inputs;
  };

  const removeInputListeners = () => {
    inputsRef.current.forEach(input => {
      input.removeEventListener('focusin', handleFocusIn);
      input.removeEventListener('focusout', handleFocusOut as EventListener);
    });
    inputsRef.current = [];
  };

  useEffect(() => {
    if (isOpen && modalId === currentId) {
      requestAnimationFrame(() => {
        const dialog = document.querySelector('[role="dialog"]');
        if (dialog instanceof HTMLElement) {
          dialogRef.current = dialog;
          addInputListeners(dialog);
        }
      });
    }

    return () => {
      removeInputListeners();
      dialogRef.current = null;
    };
  }, [isOpen, modalId, currentId]);

  return { isInputFocused };
};
